// PublicMarketplaceStore — /marketplace/store/:slug (buyer-facing catalog)
// Wave 57: Flash Deal, Delivery ETA, Vocal for Local, VIP Club, Promo Videos/Images, Visitor Counter, Floating WA
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageCircle, MapPin, Shield, Play, ShoppingBag, Star, ChevronLeft, Calendar as CalendarIcon, Eye } from 'lucide-react';
import api from '../utils/api';
import StoreNameplate from '../components/StoreNameplate';
import StoreStatusBadge from '../components/StoreStatusBadge';
import BookAppointmentModal from './marketplace/BookAppointmentModal';

const API = process.env.REACT_APP_BACKEND_URL || '';
const abs = (u) => (!u ? '' : u.startsWith('http') ? u : `${API}${u}`);
const digits = (p) => String(p || '').replace(/[^0-9]/g, '');

const ytEmbed = (u) => {
  if (!u) return null;
  const m = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};
const ytThumb = (u) => {
  if (!u) return null;
  const m = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : null;
};

// Instagram post/reel embed URL
const igEmbed = (u) => {
  if (!u) return null;
  const m = u.match(/instagram\.com\/(p|reel|tv)\/([\w-]+)/);
  if (!m) return null;
  return `https://www.instagram.com/${m[1]}/${m[2]}/embed/`;
};

// Detect URL type for rendering
const mediaType = (u) => {
  if (!u) return null;
  if (ytEmbed(u)) return 'youtube';
  if (igEmbed(u)) return 'instagram';
  return 'video';
};

// ⚡ Flash Deal Countdown Banner
function FlashDealBanner({ text, expires }) {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    if (!expires) return;
    const tick = () => {
      const diff = new Date(expires).getTime() - Date.now();
      if (diff <= 0) { setTimeLeft('EXPIRED'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expires]);
  if (!text || timeLeft === 'EXPIRED') return null;
  return (
    <div className="mx-3 mt-3 rounded-xl overflow-hidden border-2 border-rose-400/60"
         style={{ background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 60%, #7f1d1d 100%)' }}
         data-testid="mkt-store-flash-deal">
      <div className="p-3 flex items-center gap-3">
        <div className="text-2xl animate-pulse">⚡</div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-black text-rose-200 uppercase tracking-widest">Flash Deal · Limited Time!</div>
          <div className="text-sm font-black text-white truncate">{text}</div>
        </div>
        {expires && timeLeft && (
          <div className="shrink-0 text-center bg-black/40 px-3 py-1.5 rounded-lg border border-rose-400/30">
            <div className="text-[8px] text-rose-200 uppercase">Ends In</div>
            <div className="text-lg font-black text-yellow-300 tabular-nums leading-tight">{timeLeft}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// 📸 Product Image + Video Tab Switcher in modal
function PickMediaView({ pick }) {
  const [mediaTab, setMediaTab] = useState('image');
  const yt = useMemo(() => ytEmbed(pick.video_url), [pick.video_url]);
  const hasVideo = !!pick.video_url;
  const [imgIdx, setImgIdx] = useState(0);
  const images = pick.images || [];

  return (
    <div className="relative bg-black">
      {/* Content */}
      {mediaTab === 'video' && hasVideo ? (
        <div className="w-full">
          {yt
            ? <iframe title="product-video" src={yt + '?autoplay=1'} className="w-full aspect-video"
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            : <video controls src={abs(pick.video_url)} className="w-full aspect-video bg-black" playsInline autoPlay />
          }
        </div>
      ) : (
        images[imgIdx]
          ? <img src={abs(images[imgIdx])} alt="" className="w-full aspect-square object-cover" />
          : <div className="w-full aspect-square bg-slate-800 flex items-center justify-center text-6xl">📦</div>
      )}

      {/* Tab switcher */}
      {hasVideo && (
        <div className="absolute top-2 right-2 flex gap-1 bg-black/70 rounded-full px-2 py-1 z-10">
          <button onClick={() => setMediaTab('image')}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-black transition ${mediaTab === 'image' ? 'bg-white text-slate-900' : 'text-white/70'}`}>
            📸 Photo
          </button>
          <button onClick={() => setMediaTab('video')}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-black transition ${mediaTab === 'video' ? 'bg-white text-slate-900' : 'text-white/70'}`}>
            🎬 Video
          </button>
        </div>
      )}

      {/* Image thumbnails strip */}
      {mediaTab === 'image' && images.length > 1 && (
        <div className="absolute bottom-2 inset-x-0 flex gap-1 px-2 justify-center">
          {images.slice(0, 6).map((im, i) => (
            <button key={i} onClick={() => setImgIdx(i)}
                    className={`w-8 h-8 rounded border-2 overflow-hidden shrink-0 transition ${imgIdx === i ? 'border-purple-400' : 'border-white/30'}`}>
              <img src={abs(im)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// 🔨 Live Auction Card — countdown + bid CTA
function AuctionCard({ auction }) {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const tick = () => {
      const diff = new Date(auction.end_at).getTime() - Date.now();
      if (diff <= 0) { setTimeLeft('Ended'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h}h ${m}m ${s}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [auction.end_at]);
  const currentBid = auction.current_bid_at || auction.start_price_at || 0;
  return (
    <div className="rounded-lg border border-purple-400/25 bg-purple-500/5 p-2.5 flex items-center gap-2" data-testid={`mkt-store-auction-${auction.id}`}>
      {auction.images?.[0] && (
        <img src={abs(auction.images[0])} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0 border border-slate-700" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-black truncate">{auction.title}</div>
        <div className="text-sm font-black text-yellow-300">{currentBid} AT</div>
        <div className="text-[9px] opacity-60">⏰ <span className="text-purple-300 font-black">{timeLeft}</span> · 🔨 {auction.bid_count || 0} bids</div>
      </div>
      <a href={`/marketplace?tab=auctions&aid=${auction.id}`}
         className="shrink-0 px-2.5 py-1.5 rounded-lg bg-purple-600 text-white text-[10px] font-black hover:bg-purple-500 transition">
        Bid Now →
      </a>
    </div>
  );
}

export default function PublicMarketplaceStore() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [pick, setPick] = useState(null);
  const [installEvent, setInstallEvent] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [filter, setFilter] = useState({ kind: '', search: '' });
  const [visitorCount, setVisitorCount] = useState(null);
  const [storeAuctions, setStoreAuctions] = useState([]);

  useEffect(() => {
    api.get(`/marketplace/store/public/${slug}`)
      .then((r) => {
        setData(r.data);
        setVisitorCount(r.data?.store?.visitor_count || 0);
      })
      .catch((e) => {
        const code = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (code === 410) setErr({ kind: 'grace', message: detail || 'Display not available due to non-payment or renewal pending' });
        else if (code === 451) setErr({ kind: 'admin', message: detail || 'Hidden by admin · contact support' });
        else setErr({ kind: 'other', message: detail || 'Store not found' });
      });
  }, [slug]);

  // Fetch live auctions for this store
  useEffect(() => {
    if (!data?.store?.user_id) return;
    const uid = data.store.user_id;
    api.get('/marketplace/auctions?status=live&limit=50')
      .then((r) => setStoreAuctions((r.data.auctions || []).filter((a) => a.seller_id === uid)))
      .catch(() => {});
  }, [data?.store?.user_id]);

  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  useEffect(() => {
    if (!pick) return;
    api.post('/marketplace/track', { listing_id: pick.id, kind: 'view' }).catch(() => {});
  }, [pick]);

  const store = data?.store || {};

  // Advanced features gate: standard+ plans OR admin-unlocked for basic plan
  const isAdvanced = ['standard', 'prime', 'vip', 'standard_yearly', 'prime_yearly', 'vip_yearly'].includes(store.plan_tier)
    || store.advanced_features_enabled === true;

  const listings = useMemo(() => {
    let ls = data?.listings || [];
    if (filter.kind) ls = ls.filter((l) => l.kind === filter.kind);
    if (filter.search.trim()) {
      const q = filter.search.toLowerCase();
      ls = ls.filter((l) => (l.title || '').toLowerCase().includes(q) || (l.description || '').toLowerCase().includes(q));
    }
    return ls;
  }, [data, filter]);

  const openWA = (l) => {
    api.post('/marketplace/track', { listing_id: l.id, kind: 'lead' }).catch(() => {});
    const p = digits(store.whatsapp);
    const shareOg = `${API}/api/share/marketplace/listing/${l.id}`;
    const msg = `Namaste ${store.shop_name || ''}, maine aapka item "${l.title}" (ID: ${l.id.slice(0, 8)}, Price: ${l.price_at} AT) Anant Marketplace par dekha. Mujhe ye kharidna hai: ${shareOg}`;
    window.open(p ? `https://wa.me/${p}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const shareStore = async (kind) => {
    const shareOgUrl = `${API}/api/share/marketplace/store/${slug}`;
    const text = `${store.shop_name || 'Anant Marketplace store'} - ${store.tagline || ''}\n${shareOgUrl}`;
    if (kind === 'wa') return window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
    if (kind === 'fb') return window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareOgUrl)}`, '_blank');
    if (kind === 'ig') return window.open('https://www.instagram.com/', '_blank');
    if (kind === 'native' && navigator.share) { try { await navigator.share({ title: store.shop_name, text, url: shareOgUrl }); } catch {} return; }
    navigator.clipboard.writeText(text); toast.success('Link copied!');
  };

  const installPWA = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
  };

  if (err) {
    const kind = err.kind || 'other';
    const ui = kind === 'admin'
      ? { emoji: '🚫', title: 'Hidden by admin', color: 'bg-rose-500' }
      : kind === 'grace'
        ? { emoji: '⏰', title: 'Display not available', color: 'bg-amber-500' }
        : { emoji: '🏪', title: 'Store nahi mila', color: 'bg-slate-700' };
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6" data-testid={`public-store-err-${kind}`}>
        <div className="text-center max-w-md">
          <div className="text-6xl mb-3">{ui.emoji}</div>
          <div className={`inline-block px-3 py-1 rounded-full ${ui.color} text-white text-xs font-black mb-3`}>{ui.title}</div>
          <div className="opacity-80 text-sm">{err.message}</div>
          {kind === 'grace' && <a href="/marketplace" className="inline-block mt-4 px-4 py-2 rounded-xl bg-emerald-500 text-slate-900 text-xs font-black">💳 Renew Now</a>}
          <Link to="/marketplace" className="inline-block mt-2 ml-2 px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-black">← Back</Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">⏳ Loading store…</div>;

  return (
    <div className="min-h-screen text-white pb-32"
         style={{ background: 'linear-gradient(180deg, #1E1B4B 0%, #0f172a 60%, #020617 100%)' }}
         data-testid="public-marketplace-store">

      {/* Digital Nameplate Hero */}
      {(store.nameplate || store.tagline) && (
        <div className="max-w-4xl mx-auto px-3 mt-3">
          <StoreNameplate nameplate={store.nameplate} shopName={store.shop_name}
                          verified={store.verified} sponsored={!!store.sponsor_until}
                          tagline={store.tagline} />
          {store.store_status?.value && (
            <div className="flex justify-center mt-3">
              <StoreStatusBadge status={store.store_status} size="lg"
                                onClick={(store.appointment_config?.slots_per_day > 0 && store.store_status.value === 'book_appointment')
                                          ? () => setShowBooking(true) : undefined} />
            </div>
          )}
          {store.appointment_config?.slots_per_day > 0 && (
            <div className="mt-4 flex justify-center">
              <button onClick={() => setShowBooking(true)} data-testid="mkt-store-book-hero"
                      className="group relative px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-500 text-slate-900 font-black text-sm shadow-2xl shadow-emerald-500/50 hover:scale-105 transition-transform">
                <CalendarIcon size={16} className="inline mr-2" />
                Book Appointment · from {store.appointment_config.price_at} AT
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-rose-500 animate-ping" />
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-rose-500" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Banner + Logo */}
      <div className="relative">
        {store.banner_url
          ? <img src={abs(store.banner_url)} alt="" className="w-full h-40 sm:h-56 object-cover" />
          : <div className="w-full h-40 sm:h-56" style={{ background: 'linear-gradient(135deg, #4338CA, #7C3AED, #DB2777)' }} />
        }
        <div className="absolute inset-0 bg-black/40" />
        <Link to="/marketplace" data-testid="mkt-store-back" className="absolute top-3 left-3 w-9 h-9 rounded-full bg-black/70 text-white flex items-center justify-center">
          <ChevronLeft size={18} />
        </Link>

        {/* Visitor Counter Chip */}
        {visitorCount > 0 && (
          <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/70 text-white text-[10px] font-black border border-white/20 backdrop-blur-sm"
               data-testid="mkt-store-visitor-count">
            <Eye size={10} className="text-emerald-400" />
            <span className="text-emerald-300">{(visitorCount || 0).toLocaleString('en-IN')}</span>
            <span className="opacity-60">visits</span>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 p-4 flex items-end gap-3">
          {store.logo_url && (
            <img src={abs(store.logo_url)} alt="" className="w-16 h-16 rounded-xl border-2 border-white object-cover shrink-0" />
          )}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-black flex items-center gap-2 flex-wrap" data-testid="mkt-store-name">
              {store.shop_name}
              {store.verified && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500 text-white font-black flex items-center gap-0.5"><Shield size={10} /> Verified</span>}
              {store.sponsor_until && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 font-black flex items-center gap-0.5"><Star size={10} /> Sponsored</span>}
              {isAdvanced && store.vocal_for_local && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500 text-white font-black">🇮🇳 Vocal for Local</span>}
            </h1>
            {store.tagline && <div className="text-xs opacity-80 truncate">{store.tagline}</div>}
            <div className="text-[11px] opacity-90 mt-1 flex flex-wrap gap-2">
              {store.city && <span className="flex items-center gap-1"><MapPin size={11} /> {store.city}</span>}
              {store.category && <span>· {store.category}</span>}
              {store.whatsapp && (
                <a href={`https://wa.me/${digits(store.whatsapp)}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-emerald-200">
                  <MessageCircle size={11} /> {store.whatsapp}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ⚡ Flash Deal Banner */}
      {isAdvanced && <FlashDealBanner text={store.flash_deal_text} expires={store.flash_deal_expires} />}

      {/* 🚚 Delivery ETA + 🇮🇳 Vocal for Local */}
      {isAdvanced && (store.delivery_eta || store.vocal_for_local) && (
        <div className="max-w-4xl mx-auto px-3 mt-3 flex flex-wrap gap-2">
          {store.delivery_eta && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-yellow-400/50 text-xs font-black"
                 style={{ background: 'linear-gradient(135deg, #78350f, #92400e)' }}
                 data-testid="mkt-store-delivery-eta">
              <span className="text-xl">⚡</span>
              <div>
                <div className="text-[8px] text-yellow-200 uppercase tracking-wider">Delivery ETA</div>
                <div className="text-yellow-300 font-black">{store.delivery_eta}</div>
              </div>
            </div>
          )}
          {store.vocal_for_local && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-orange-400/50 text-xs font-black"
                 style={{ background: 'linear-gradient(135deg, #7c2d12, #9a3412)' }}
                 data-testid="mkt-store-vocal-local">
              <span className="text-xl">🇮🇳</span>
              <div>
                <div className="text-[8px] text-orange-200 uppercase tracking-wider">Vocal for Local · Made in India</div>
                <div className="text-orange-300">{store.vocal_story || 'Support Local Business'}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Share Strip + Store Details + Social Block */}
      <div className="max-w-4xl mx-auto px-3 mt-3">
        {!localStorage.getItem('token') && (
          <a href="/login" data-testid="mkt-store-login-cta"
             className="block mb-2 text-center py-2 rounded-xl bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 text-slate-900 text-xs font-black shadow-lg">
            🔐 Login for Safe Buying · Promo Offers · Local for Vocal Offers 🇮🇳
          </a>
        )}
        <div className="rounded-xl bg-slate-900/70 border border-slate-700 p-2.5 flex flex-wrap items-center gap-2 text-xs" data-testid="mkt-store-actions">
          <span className="opacity-70 font-black text-[10px] uppercase">Share:</span>
          <button onClick={() => shareStore('wa')} className="px-2 py-1 rounded bg-green-600 text-white text-[10px] font-black" data-testid="mkt-store-share-wa">📱 WhatsApp</button>
          <button onClick={() => shareStore('fb')} className="px-2 py-1 rounded bg-blue-600 text-white text-[10px] font-black">📘 Facebook</button>
          <button onClick={() => shareStore('ig')} className="px-2 py-1 rounded text-white text-[10px] font-black" style={{ background: 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)' }}>📸 Insta</button>
          <button onClick={() => shareStore('native')} className="px-2 py-1 rounded bg-slate-700 text-white text-[10px] font-black">📲 More</button>
          {(store.maps_link || store.address || (store.lat && store.lng)) && (
            <a href={store.maps_link ? store.maps_link
                     : store.lat && store.lng ? `https://www.google.com/maps/search/?api=1&query=${store.lat},${store.lng}`
                     : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.address)}`}
               target="_blank" rel="noopener noreferrer" data-testid="mkt-store-visit-location"
               className="ml-auto px-2 py-1 rounded bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-[10px] font-black flex items-center gap-1 hover:shadow-lg hover:shadow-blue-500/40 transition">
              🗺️ Visit Store
            </a>
          )}
          {store.appointment_config?.slots_per_day > 0 && (
            <button onClick={() => setShowBooking(true)} data-testid="mkt-store-book-appointment"
                    className="ml-auto px-2.5 py-1.5 rounded bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-900 text-[10px] font-black flex items-center gap-1">
              <CalendarIcon size={11} /> Book Appointment
            </button>
          )}
          {installEvent && (
            <button onClick={installPWA} data-testid="mkt-store-pwa-install"
                    className="ml-auto px-2 py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">📱 Add to Home</button>
          )}
        </div>

        {/* Store Details */}
        {(store.description || store.hours_text || store.close_days || store.terms_text) && (
          <div className="rounded-xl bg-slate-900/70 border border-purple-400/30 p-3 mt-2 space-y-1.5 text-xs" data-testid="mkt-store-details-public">
            {store.description && <div className="whitespace-pre-wrap opacity-90">{store.description}</div>}
            <div className="flex flex-wrap gap-3 pt-1 text-[11px]">
              {store.hours_text && <div><span className="opacity-60">🕒 Timings:</span> <b>{store.hours_text}</b></div>}
              {store.close_days && <div><span className="opacity-60">📅 Closed:</span> <b>{store.close_days}</b></div>}
            </div>
            {store.terms_text && (
              <details className="pt-1"><summary className="text-[10px] opacity-70 cursor-pointer">📜 Store Terms &amp; Conditions</summary>
                <div className="mt-1 text-[11px] opacity-80 whitespace-pre-wrap">{store.terms_text}</div>
              </details>
            )}
          </div>
        )}

        {/* Social Media & Custom Buttons */}
        <PublicStoreSocialBlock store={store} />

        {/* 👑 VIP Club Banner */}
        {isAdvanced && store.vip_club_text && (
          <div className="mt-3 rounded-xl overflow-hidden border border-purple-400/50 p-3 flex items-center gap-3"
               style={{ background: 'linear-gradient(135deg, #2e1065, #4c1d95)' }}
               data-testid="mkt-store-vip-club">
            <span className="text-2xl shrink-0">👑</span>
            <div className="flex-1 min-w-0">
              <div className="text-[8px] font-black text-purple-200 uppercase tracking-widest">VIP Club · Exclusive Cashback</div>
              <div className="text-xs font-black text-white">{store.vip_club_text}</div>
            </div>
            {store.whatsapp && (
              <a href={`https://wa.me/${digits(store.whatsapp)}?text=${encodeURIComponent('Namaste! Main VIP Club join karna chahta hoon.')}`}
                 target="_blank" rel="noreferrer"
                 className="shrink-0 px-3 py-1.5 rounded-xl bg-purple-400 text-slate-900 text-[10px] font-black hover:bg-purple-300 transition">
                Join Now →
              </a>
            )}
          </div>
        )}

        {/* 🎬 Promo Videos Section — max 2 shown, rest on separate page */}
        {isAdvanced && store.promo_videos?.filter(Boolean).length > 0 && (
          <div className="mt-3 rounded-xl bg-slate-900/70 border border-rose-400/30 overflow-hidden" data-testid="mkt-store-promo-videos">
            <div className="px-3 pt-2.5 pb-1 text-[10px] font-black text-rose-300 uppercase tracking-wider">🎬 Promo Videos</div>
            <div className="space-y-2 px-2 pb-2">
              {store.promo_videos.filter(Boolean).slice(0, 2).map((url, i) => {
                const embedUrl = ytEmbed(url);
                const igUrl = igEmbed(url);
                return (
                  <div key={i} className="rounded-lg overflow-hidden border border-slate-700 bg-black">
                    {embedUrl
                      ? <iframe title={`promo-video-${i}`} src={embedUrl} className="w-full aspect-video"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                      : igUrl
                        ? <div className="relative">
                            <iframe title={`ig-reel-${i}`} src={igUrl} className="w-full"
                                    style={{ minHeight: '500px', border: 'none' }}
                                    scrolling="no" allowTransparency="true" />
                            <a href={url} target="_blank" rel="noreferrer"
                               className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/70 text-white text-[9px] font-black">
                              📸 Open on Instagram
                            </a>
                          </div>
                        : <video controls src={abs(url)} className="w-full aspect-video bg-black" playsInline />
                    }
                  </div>
                );
              })}
            </div>
            {/* If more than 2 videos → View All page */}
            {store.promo_videos.filter(Boolean).length > 2 && (
              <Link to={`/marketplace/store/${slug}/videos`}
                    data-testid="mkt-store-view-all-videos"
                    className="flex items-center justify-center gap-2 py-2.5 bg-rose-900/40 hover:bg-rose-900/60 border-t border-rose-400/20 text-rose-300 text-[10px] font-black transition">
                🎬 View All {store.promo_videos.filter(Boolean).length} Videos →
              </Link>
            )}
          </div>
        )}

        {/* 🖼️ Offer / Promo Images */}
        {isAdvanced && store.promo_images?.filter(Boolean).length > 0 && (
          <div className="mt-3 rounded-xl bg-slate-900/70 border border-amber-400/30 overflow-hidden" data-testid="mkt-store-promo-images">
            <div className="px-3 pt-2.5 pb-1 text-[10px] font-black text-amber-300 uppercase tracking-wider">🖼️ Special Offers &amp; Promotions</div>
            <div className="flex gap-2 overflow-x-auto pb-2 px-2" style={{ scrollbarWidth: 'thin' }}>
              {store.promo_images.filter(Boolean).map((url, i) => (
                <img key={i} src={abs(url)} alt={`offer-${i + 1}`}
                     className="h-48 w-auto max-w-xs rounded-lg border border-slate-700 object-contain bg-slate-800 shrink-0 cursor-pointer hover:scale-105 transition"
                     onClick={() => window.open(abs(url), '_blank')}
                     data-testid={`mkt-store-offer-img-${i}`} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 🔨 Live Auctions Widget */}
      {storeAuctions.length > 0 && (
        <div className="max-w-4xl mx-auto px-3 mt-3 rounded-xl bg-slate-900/70 border border-purple-400/40 overflow-hidden"
             data-testid="mkt-store-live-auctions">
          <div className="px-3 pt-2.5 pb-1 flex items-center justify-between">
            <span className="text-[10px] font-black text-purple-300 uppercase tracking-wider">🔨 Live Auctions · Abhi Bid Karo!</span>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-purple-500/30 text-purple-300 font-black animate-pulse">{storeAuctions.length} LIVE</span>
          </div>
          <div className="space-y-1.5 px-2 pb-2">
            {storeAuctions.map((a) => <AuctionCard key={a.id} auction={a} />)}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="max-w-4xl mx-auto px-3 mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <input value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} placeholder="🔍 Search products/services"
               data-testid="mkt-store-search"
               className="col-span-2 sm:col-span-3 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <select value={filter.kind} onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
                className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" data-testid="mkt-store-filter-kind">
          <option value="">All</option>
          <option value="product">📦 Products</option>
          <option value="service">🔧 Services</option>
          <option value="showroom">🚘 Showrooms</option>
        </select>
      </div>

      {/* 📦 Listings Grid */}
      <div className="max-w-4xl mx-auto px-3 mt-3">
        <div className="text-xs uppercase opacity-70 font-black mb-2 flex items-center gap-1">
          <ShoppingBag size={12} /> Catalog ({listings.length})
        </div>
        {listings.length === 0 && <div className="text-center opacity-60 py-10 text-xs">Koi listing nahi mila.</div>}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {listings.map((l) => {
            const thumb = ytThumb(l.video_url);
            const hasVideo = !!l.video_url;
            return (
              <button key={l.id} id={`listing-${l.id}`}
                      onClick={() => setPick(l)}
                      data-testid={`mkt-store-listing-${l.id}`}
                      className={`rounded-2xl border-2 overflow-hidden text-left transition group ${l.featured ? 'border-purple-400/70 bg-purple-500/10' : 'border-slate-700 bg-slate-900/70'} hover:border-purple-300`}>
                <div className="aspect-square bg-slate-800 relative overflow-hidden">
                  {/* Video thumbnail takes priority over product image */}
                  {hasVideo && thumb
                    ? <img src={thumb} alt="" className="w-full h-full object-cover" />
                    : l.images?.[0]
                      ? <img src={abs(l.images[0])} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-4xl">📦</div>
                  }
                  {l.featured && <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-purple-500 text-white text-[9px] font-black">⭐ Featured</span>}
                  {/* Play overlay on hover */}
                  {hasVideo && (
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-2xl">
                        <Play size={22} className="text-slate-900 ml-1" />
                      </div>
                    </div>
                  )}
                  {hasVideo && (
                    <span className="absolute bottom-1 right-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-black">
                      <Play size={9} /> Video
                    </span>
                  )}
                </div>
                <div className="p-2 space-y-0.5">
                  <div className="text-xs font-black truncate">{l.title}</div>
                  <div className="text-sm font-black text-yellow-300">{l.price_at} AT</div>
                  <div className="text-[9px] opacity-60">👁 {l.views || 0} · 💬 {l.leads || 0}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 📋 Product Detail Modal */}
      {pick && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-end sm:items-center justify-center p-2"
             onClick={() => setPick(null)} data-testid="mkt-store-modal">
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-lg rounded-2xl bg-slate-900 border-2 border-purple-400/60 max-h-[95vh] overflow-y-auto">
            {/* Image + Video with tab switcher */}
            <PickMediaView pick={pick} />
            <button onClick={() => setPick(null)}
                    className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/70 text-white font-black z-10"
                    data-testid="mkt-store-modal-close">✕</button>
            <div className="p-4 space-y-3">
              <div>
                <div className="text-lg font-black">{pick.title}</div>
                <div className="text-xl font-black text-yellow-300 mt-1">{pick.price_at} AT</div>
                <div className="text-[10px] opacity-70">{pick.kind} · {pick.city || store.city || ''}</div>
              </div>
              {pick.description && <div className="text-xs opacity-90 whitespace-pre-wrap">{pick.description}</div>}
              <button onClick={() => openWA(pick)} data-testid={`mkt-store-order-${pick.id}`}
                      className="w-full py-3 rounded-2xl bg-green-600 hover:bg-green-700 text-white text-sm font-black flex items-center justify-center gap-2 transition">
                <MessageCircle size={16} /> Order via WhatsApp
              </button>
              <button onClick={() => setPick(null)}
                      className="w-full py-2 rounded-xl bg-slate-800 text-slate-300 text-xs font-black">✕ Close</button>
            </div>
          </div>
        </div>
      )}

      {showBooking && <BookAppointmentModal store={store} onClose={() => setShowBooking(false)} />}

      {store.disclaimer_text && (
        <div className="max-w-4xl mx-auto mt-4 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-[10px] opacity-70 italic whitespace-pre-wrap"
             data-testid="mkt-store-disclaimer">
          ⚠️ Disclaimer: {store.disclaimer_text}
        </div>
      )}

      {/* 💬 Floating WhatsApp Chat & Negotiate Button */}
      {store.whatsapp && (
        <a href={`https://wa.me/${digits(store.whatsapp)}?text=${encodeURIComponent(`Namaste! ${store.shop_name || ''} se kuch products ke baare mein baat karni thi aur negotiate karna tha.`)}`}
           target="_blank" rel="noreferrer"
           data-testid="mkt-store-floating-wa"
           className="fixed bottom-6 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-full text-white font-black shadow-2xl hover:scale-105 transition-transform"
           style={{ background: 'linear-gradient(135deg, #15803d, #16a34a)', boxShadow: '0 8px 32px -8px rgba(22,163,74,0.9)' }}>
          <MessageCircle size={18} />
          <span className="text-xs font-black sm:inline hidden">Chat &amp; Negotiate</span>
          <span className="text-xs font-black sm:hidden">Chat</span>
        </a>
      )}
    </div>
  );
}


// Wave 51 · Social Media & Custom Buttons block
function PublicStoreSocialBlock({ store }) {
  const SOCIAL = [
    { k: 'zomato',     icon: '🍽️', label: 'Zomato',      bg: 'bg-red-600' },
    { k: 'swiggy',     icon: '🥘', label: 'Swiggy',      bg: 'bg-orange-500' },
    { k: 'blinkit',    icon: '⚡', label: 'Blinkit',     bg: 'bg-yellow-500 text-slate-900' },
    { k: 'zepto',      icon: '🚀', label: 'Zepto',       bg: 'bg-fuchsia-600' },
    { k: 'instagram',  icon: '📸', label: 'Instagram',   bg: 'bg-gradient-to-br from-purple-500 to-pink-500' },
    { k: 'facebook',   icon: '📘', label: 'Facebook',    bg: 'bg-blue-600' },
    { k: 'youtube',    icon: '▶️', label: 'YouTube',     bg: 'bg-red-500' },
    { k: 'website',    icon: '🌍', label: 'Website',     bg: 'bg-sky-600' },
    { k: 'google_maps',icon: '📍', label: 'Google Maps', bg: 'bg-emerald-600' },
    { k: 'justdial',   icon: '📒', label: 'JustDial',    bg: 'bg-amber-600' },
  ];
  const links = store?.social_links || {};
  const custom = store?.custom_buttons || [];
  const active = SOCIAL.filter((s) => (links[s.k] || '').trim());
  if (active.length === 0 && custom.length === 0) return null;
  return (
    <div className="rounded-xl bg-slate-900/70 border border-pink-400/30 p-3 mt-2 space-y-2" data-testid="mkt-store-social-public">
      <div className="text-[11px] font-black text-pink-300 uppercase">🌐 Connect &amp; Order Elsewhere</div>
      <div className="flex flex-wrap gap-1.5">
        {active.map((s) => (
          <a key={s.k} href={links[s.k]} target="_blank" rel="noreferrer"
             data-testid={`mkt-store-social-public-${s.k}`}
             className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full ${s.bg} text-white text-xs font-black shadow hover:scale-105 transition`}>
            <span>{s.icon}</span><span>{s.label}</span>
          </a>
        ))}
        {custom.map((b, i) => (
          b.url
            ? <a key={`cb-${i}`} href={b.url} target="_blank" rel="noreferrer"
                 data-testid={`mkt-store-custom-public-${i}`}
                 className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-700 text-white text-xs font-black shadow hover:scale-105 transition">
                <span>{b.icon || '🔘'}</span><span>{b.label || 'Open'}</span>
              </a>
            : null
        ))}
      </div>
    </div>
  );
}
